import type { TerrainSampler } from "../world/types";

/*
 * The ground the player has actually walked, cell by cell.
 *
 * The journal's sibling instrument (`docs/plans/phase-5-plan.md`, decision 10):
 * the journal holds what the player was *told* and this holds where they have
 * *been*, and both are client-side for the same reason — the simulation has no
 * opinion about either, and a hashed record of one would be a hashed record of
 * a thing the world does not know (Phase 3 decision 10). Like the journal, it
 * dies with the tab: the replay is the only save format this project has, and a
 * map persisted anywhere else would be a second one.
 *
 * **The whole of the cartography honesty rule lives here.** The terrain sampler
 * answers `groundLevel(x, y)` for every column in the world whether or not
 * anybody has been near it (`world/types.ts`) — it re-derives the elevation
 * field from the seed — so the map cannot be made honest by asking the world
 * nicely. What makes it honest is the *mask*: this store is the only thing that
 * says which cells may be drawn at all, and nothing outside a cell it holds
 * reaches the paper. Fog of exploration, and the fog is the default.
 *
 * # What a cell is, and why it is eight tiles
 *
 * A coarse grid, and the coarseness is the look as much as the budget. Eight
 * tiles is half a chunk (`world/types.ts`'s `CHUNK_SIZE` is 16), which is about
 * two seconds of walking and roughly the size of a thing a surveyor would draw
 * as one blot — fine enough that a road bends and a valley reads as a valley,
 * coarse enough that the edge of what is known looks hand-torn rather than
 * pixel-exact. Settlements in a live world stand some six hundred tiles apart
 * (`witness/witness.ts`), so a road between two of them is seventy-five cells:
 * a line with shape in it, not a straight run of two.
 *
 * # What is seen, and what is merely known to exist
 *
 * Two sets, and the difference is the honesty:
 *
 * - **walked** — a cell the player has physically stood in. The trail is the
 *   route through them, in order, and its last corner is where the player is.
 * - **seen** — walked, plus the cells within {@link SEEN_RADIUS_CELLS} of one.
 *   A player standing on a hill can draw the ground around them, and this is
 *   how much of it they are credited with. It is deliberately conservative:
 *   three cells is twenty-four tiles, and the fog in this client swallows
 *   everything past eighty (`world/wasm.ts`'s view distance), so the map claims
 *   less than the eye can see rather than more.
 *
 * `walked` is always a subset of `seen`, which is the invariant every drawing
 * rule downstream rests on (`map/survey.ts`).
 *
 * There is no DOM here and no Three.js: this is the state and the rules over it,
 * which is what makes it checkable in `dev/selfcheck.ts` — the split
 * `journal/journal.ts` is written under, kept.
 */

/**
 * How many tiles on a side one cell of the map covers.
 *
 * Half a chunk. See the header for why coarse is a decision rather than a
 * saving — and note that it is the *only* place the granularity is spelled:
 * everything below counts in cells and converts at the edges.
 */
export const CELL_TILES = 8;

/**
 * How far past the ground underfoot a player is credited with seeing, in cells.
 *
 * Three, which is twenty-four tiles straight out and about thirty-four across a
 * corner. Both numbers are well inside the fog this client draws at eighty
 * (`render/scene.ts`), which is the shape of the choice: the map is allowed to
 * know less than the player could see and never more. Measured on cell centres
 * as a disc rather than a square, so the fringe is round and the diagonal does
 * not quietly claim half as far again as the straight.
 */
export const SEEN_RADIUS_CELLS = 3;

/** The same radius in tiles, for anything that reasons in world units. */
export const SEEN_RADIUS_TILES = SEEN_RADIUS_CELLS * CELL_TILES;

/**
 * The most cells one session's map may ever hold. PROVISIONAL.
 *
 * A bound on memory rather than a design statement, and it is sized so that it
 * never bites in a session anybody will play: a player walks about five tiles a
 * second, so an hour of walking in a straight line is eighteen thousand tiles —
 * two and a quarter thousand walked cells, and about fifteen thousand seen ones
 * with the fringe. Thirty thousand is twice that, and it is a few megabytes of
 * numbers at the very worst.
 *
 * **The policy at the cap is saturation, not eviction**, and that is the honest
 * half of the choice. Forgetting the oldest ground would give a map that erases
 * where the player has been while they watch — a lie about their own morning —
 * where refusing new ground gives a map that stops growing and *says so*
 * ({@link WalkedGround.full}, drawn as a line on the paper). A sheet of paper
 * runs out; it does not un-draw itself.
 *
 * The walked cell is always taken before its fringe, so what saturates first is
 * the ground the player merely glimpsed rather than the ground under their feet.
 */
export const MAX_MAP_CELLS = 30_000;

/**
 * How many corners of the player's route the trail keeps. PROVISIONAL.
 *
 * One corner per cell crossed, so at a sprint (`player/controller.ts`'s 7.5
 * tiles a second over eight-tile cells) this is about an hour of walking.
 *
 * **This is the one thing in the store that forgets, and the distinction is
 * deliberate: the ground is remembered and the route is not.** A cell walked
 * stays shaded for the session; the line through it frays away at the far end
 * once the walk is long enough, which is what a route drawn on a page does and
 * costs the map nothing it is claiming — the shading underneath is still there,
 * and it is the shading that says where the player has been. What the trail is
 * *for* is the near end: the last corner is where they are standing, which is
 * the whole of this client's wayfinding (`map/panel.ts` — no compass, no dot).
 */
export const MAX_TRAIL_POINTS = 4096;

/**
 * How far a cell index may be from the origin before the store refuses it.
 *
 * The keys below pack two signed cell indices into one number, and a world
 * bigger than this would silently alias one cell onto another — two places on
 * one blot, with nothing to say it had happened. So the packing is guarded and
 * the refusal is counted ({@link WalkedGround.refused}). Thirty-two thousand
 * cells is a quarter of a million tiles from the origin in each direction,
 * which is far outside anything worldgen produces today.
 */
const CELL_INDEX_LIMIT = 32_768;

/** The span of one packed axis. See {@link CELL_INDEX_LIMIT}. */
const CELL_KEY_SPAN = CELL_INDEX_LIMIT * 2;

/** One cell of surveyed ground, as the drawing side wants it. */
export interface MapCell {
  /** The cell's south-west corner, in world tiles. It is {@link CELL_TILES} square. */
  readonly x: number;
  readonly y: number;

  /** The elevation field at the cell's centre: `TerrainSampler.groundLevel`. */
  readonly ground: number;

  /** Whether the player stood in it, as opposed to seeing it from nearby. */
  readonly walked: boolean;
}

/** One corner of the player's own trail, in world tiles. */
export interface TrailPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * What a mark on the map can be about.
 *
 * **The seam wave 2B left and 4A landed on.** Two kinds, and the pair is the
 * point: both are things the *simulation* says are there, written onto the
 * player's own paper only where their own feet earned the ground under them
 * ({@link WalkedGround.place} is the guard, and it is the whole of it).
 *
 * - `"chronicle"` — a book standing where somebody left it (plan decision 5),
 *   marked when the player has read one or walked close enough to see it.
 * - `"ruin"` — a town still in the wreckage of the last army through it (plan
 *   decision 11). `world/types.ts` says the ruins read is the whole world
 *   rather than one settlement precisely because "a map that marks ruined towns
 *   wants all of them"; the fog is what keeps that honest, since a town whose
 *   ground the player has never crossed has no position on this paper at all
 *   (`map/survey.ts`).
 *
 * A third kind is one string here and one arm in `map/panel.ts`'s glyph switch.
 */
export type MapMarkKind = "chronicle" | "ruin";

/** Something the player put on the paper, or found and wrote down. */
export interface MapMark {
  readonly kind: MapMarkKind;

  /** Where it is, in world tiles. Refused unless it stands on ground the map knows. */
  readonly x: number;
  readonly y: number;

  /**
   * What to write beside it. The world's own words; nothing here composes one.
   *
   * Empty for a mark that wants no name — a cross on a town whose own name is
   * already written beside it, which is what a ruin is. The paper draws the
   * glyph and skips the label (`map/panel.ts`).
   */
  readonly label: string;
}

/**
 * Where the player has been, and what that entitles the map to draw.
 *
 * Fed one position at a time from wherever the client already knows where the
 * player is standing (`witness/witness.ts`'s `render`), which is once a frame
 * and costs a floor, a lookup and — on the two-second occasions the player
 * crosses into a cell they have never been in — one square of elevation
 * samples.
 */
export class WalkedGround {
  private readonly terrain: TerrainSampler;
  private readonly maxCells: number;

  /** Cells stood in, by packed key. Always a subset of {@link WalkedGround.seenCells}. */
  private readonly walkedCells = new Set<number>();

  /** Every cell that may be drawn, with the elevation to draw it at. */
  private readonly seenCells = new Map<number, number>();

  /** The route through the walked cells, oldest first. Its end is "here". */
  private readonly path: TrailPoint[] = [];

  /** The cell the last position was in, so a crossing can be told from a step. */
  private lastCell: number | null = null;

  private readonly placed: MapMark[] = [];

  /** How many marks were refused for standing on ground the map does not know. */
  private refusedMarks = 0;

  /** How many cells were refused: the cap, or the packing guard. */
  private refusedCells = 0;

  private saturated = false;
  private changes = 0;

  constructor(terrain: TerrainSampler, maxCells: number = MAX_MAP_CELLS) {
    this.terrain = terrain;
    this.maxCells = maxCells;
  }

  /**
   * How many times the map has changed.
   *
   * A revision rather than an event, for the panel: a map left open while the
   * player walks has to be redrawn when there is something new on it and not
   * every frame, and one integer compare per step is the whole cost of knowing
   * which (`witness/witness.ts`).
   */
  get revision(): number {
    return this.changes;
  }

  /** Whether the paper has run out of room. See {@link MAX_MAP_CELLS}. */
  get full(): boolean {
    return this.saturated;
  }

  get walkedCount(): number {
    return this.walkedCells.size;
  }

  get seenCount(): number {
    return this.seenCells.size;
  }

  /** How much room is left, in cells. Zero once {@link WalkedGround.full}. */
  get room(): number {
    return Math.max(0, this.maxCells - this.seenCells.size);
  }

  /** Cells and marks turned away, for the debug handle. Non-zero is worth knowing about. */
  get refused(): { readonly cells: number; readonly marks: number } {
    return { cells: this.refusedCells, marks: this.refusedMarks };
  }

  /** The player's own trail, oldest first. Its last point is where they are. */
  get trail(): readonly TrailPoint[] {
    return this.path;
  }

  /** Everything written on the paper by hand. See {@link MapMark}. */
  get marks(): readonly MapMark[] {
    return this.placed;
  }

  /**
   * Records that the player is standing here, and returns `true` if this is
   * ground the map had never been on.
   *
   * Called once a rendered frame, and it does nothing at all on almost all of
   * them: a cell is eight tiles and a sprint crosses one about once a second,
   * so the common path is a floor, a compare and a return.
   *
   * **A crossing is a trail corner whether or not the ground is new.** Walking
   * back the way you came adds no shading — that cell is already on the paper —
   * but it does move the end of the line, and the end of the line is the only
   * thing on this map that says where the player is standing (`map/panel.ts`:
   * no compass, no player dot). A trail that only recorded first visits would
   * leave its end pointing at the furthest place the session had ever reached.
   *
   * The elevation of every cell this opens up is sampled once, here, and kept:
   * the field is a function of the seed and does not move, so a cell sampled on
   * the day it was first seen is a cell sampled correctly forever — and it
   * means the drawing side is pure, which is what lets the self-checks assert
   * over exactly what the panel draws.
   */
  walk(x: number, y: number): boolean {
    const cx = cellIndex(x);
    const cy = cellIndex(y);
    const key = packCell(cx, cy);

    if (key === null) {
      this.refusedCells += 1;

      return false;
    }

    if (key === this.lastCell) {
      return false;
    }

    if (this.walkedCells.has(key)) {
      this.lastCell = key;
      this.remember({ x, y });

      return false;
    }

    // The ground underfoot before the fringe around it, deliberately: when the
    // paper runs out it is the glimpsed ground that goes, never the ground the
    // player actually stood on (see {@link MAX_MAP_CELLS}). A cell that cannot
    // be taken is not a trail corner either — every corner of the line stands
    // on ground the map holds, which is what keeps the drawing honest.
    if (!this.see(cx, cy, key)) {
      return false;
    }

    this.lastCell = key;
    this.walkedCells.add(key);
    this.remember({ x, y });

    for (let dy = -SEEN_RADIUS_CELLS; dy <= SEEN_RADIUS_CELLS; dy += 1) {
      for (let dx = -SEEN_RADIUS_CELLS; dx <= SEEN_RADIUS_CELLS; dx += 1) {
        if (dx * dx + dy * dy > SEEN_RADIUS_CELLS ** 2) {
          continue;
        }

        const fringe = packCell(cx + dx, cy + dy);

        if (fringe === null) {
          this.refusedCells += 1;

          continue;
        }

        this.see(cx + dx, cy + dy, fringe);
      }
    }

    return true;
  }

  /** Whether the player has stood in the cell this tile is in. */
  hasWalked(x: number, y: number): boolean {
    const key = packCell(cellIndex(x), cellIndex(y));

    return key !== null && this.walkedCells.has(key);
  }

  /** Whether the map may draw the cell this tile is in at all. */
  hasSeen(x: number, y: number): boolean {
    const key = packCell(cellIndex(x), cellIndex(y));

    return key !== null && this.seenCells.has(key);
  }

  /**
   * Every cell the map is entitled to draw, in no particular order.
   *
   * Rebuilt on each call rather than kept: the caller is a panel somebody
   * opened, which is `Journal.byRecord`'s reasoning exactly, and the survey it
   * feeds is rebuilt whole anyway (`map/survey.ts`).
   */
  cells(): readonly MapCell[] {
    const drawn: MapCell[] = [];

    for (const [key, ground] of this.seenCells) {
      const { cx, cy } = unpackCell(key);

      drawn.push({
        x: cx * CELL_TILES,
        y: cy * CELL_TILES,
        ground,
        walked: this.walkedCells.has(key),
      });
    }

    return drawn;
  }

  /**
   * Writes a mark on the paper, and returns `false` when it may not be there.
   *
   * **The guard is the point.** A mark is the one thing on this map that comes
   * from outside — wave 4A's chronicles — and a caller that could put a cross
   * on ground nobody has been near would be the one hole in the honesty rule.
   * So a position off the surveyed ground is refused and counted rather than
   * trusted; the caller learns it failed and the debug handle can see how often
   * ({@link WalkedGround.refused}).
   */
  place(mark: MapMark): boolean {
    if (!this.hasSeen(mark.x, mark.y)) {
      this.refusedMarks += 1;

      return false;
    }

    this.placed.push(mark);
    this.changes += 1;

    return true;
  }

  /** Forgets everything. Only the dev handle calls it, like the journal's. */
  clear(): void {
    this.walkedCells.clear();
    this.seenCells.clear();
    this.lastCell = null;
    this.path.length = 0;
    this.placed.length = 0;
    this.refusedCells = 0;
    this.refusedMarks = 0;
    this.saturated = false;
    this.changes += 1;
  }

  /**
   * Adds a corner to the trail, dropping the oldest once the line is long
   * enough ({@link MAX_TRAIL_POINTS}).
   *
   * `shift` rather than a ring buffer: it runs about once a second at a sprint
   * and only after an hour of walking, and a ring would put an index into every
   * consumer of a list that is otherwise just a list.
   */
  private remember(corner: TrailPoint): void {
    this.path.push(corner);
    this.changes += 1;

    if (this.path.length > MAX_TRAIL_POINTS) {
      this.path.shift();
    }
  }

  /**
   * Adds one cell to what may be drawn, sampling its elevation, and answers
   * whether it is there now.
   *
   * `true` for a cell already known, which is what makes the walked-before-
   * fringe ordering above work when the paper is nearly full.
   */
  private see(cx: number, cy: number, key: number): boolean {
    if (this.seenCells.has(key)) {
      return true;
    }

    if (this.seenCells.size >= this.maxCells) {
      this.saturated = true;
      this.refusedCells += 1;

      return false;
    }

    // The middle of the cell, floored to a tile: `groundLevel` is the
    // simulation's own elevation field and it is asked about columns
    // (`world/types.ts`). One sample per cell rather than an average of four —
    // the map is drawn at eight tiles to the blot and an average would be four
    // times the boundary traffic for a smoothing nobody would see.
    const centre = CELL_TILES >> 1;
    const ground = this.terrain.groundLevel(cx * CELL_TILES + centre, cy * CELL_TILES + centre);

    this.seenCells.set(key, ground);
    this.changes += 1;

    return true;
  }
}

/** Which cell a world coordinate falls in. Floors, so it is right at negative x. */
export function cellIndex(coord: number): number {
  return Math.floor(coord / CELL_TILES);
}

/**
 * Packs a cell index pair into one number, or `null` when it is off the paper.
 *
 * Two signed indices biased into `[0, CELL_KEY_SPAN)` and multiplied out, which
 * is exact in a double up to 2^32 and reverses cleanly ({@link unpackCell}).
 * A string key would work and would cost a couple of megabytes of short strings
 * at the cap; this is the same bookkeeping without them.
 */
function packCell(cx: number, cy: number): number | null {
  if (
    !Number.isFinite(cx) ||
    !Number.isFinite(cy) ||
    Math.abs(cx) >= CELL_INDEX_LIMIT ||
    Math.abs(cy) >= CELL_INDEX_LIMIT
  ) {
    return null;
  }

  return (cx + CELL_INDEX_LIMIT) * CELL_KEY_SPAN + (cy + CELL_INDEX_LIMIT);
}

/** The inverse of {@link packCell}. */
function unpackCell(key: number): { readonly cx: number; readonly cy: number } {
  return {
    cx: Math.floor(key / CELL_KEY_SPAN) - CELL_INDEX_LIMIT,
    cy: (key % CELL_KEY_SPAN) - CELL_INDEX_LIMIT,
  };
}
